import { useI18n } from '../i18n/useI18n';

/**
 * 统一的精美加载动画组件
 * @param {Object} props
 * @param {string} props.text - 加载提示文字，不传则使用默认
 * @param {string} props.size - 尺寸: 'small' | 'medium' | 'large' | 'fullscreen'
 * @param {boolean} props.fullscreen - 是否全屏显示
 */
export function LoadingSpinner({ text, size = 'medium', fullscreen = false }) {
  const { t } = useI18n();
  
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-12 h-12',
    large: 'w-16 h-16',
    fullscreen: 'w-20 h-20'
  };

  const textSizes = {
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-base',
    fullscreen: 'text-lg'
  };

  const containerClasses = fullscreen 
    ? 'fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm'
    : 'flex flex-col items-center justify-center';

  return (
    <div className={containerClasses}>
      <div className="flex flex-col items-center gap-4">
        {/* 主加载动画 */}
        <div className="relative">
          {/* 外圈 - 渐变背景 */}
          <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-r from-sky-500/20 via-indigo-500/20 to-sky-500/20 animate-pulse`} />
          
          {/* 中圈 - 旋转 */}
          <div className={`absolute inset-0 ${sizeClasses[size]} rounded-full border-2 border-transparent border-t-sky-400 border-r-indigo-400 animate-spin`} style={{ animationDuration: '1s' }} />
          
          {/* 内圈 - 反向旋转 */}
          <div className={`absolute inset-2 rounded-full border-2 border-transparent border-b-sky-300 border-l-indigo-300 animate-spin`} style={{ animationDuration: '1.5s', animationDirection: 'reverse' }} />
          
          {/* 中心点 */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 rounded-full bg-gradient-to-br from-sky-400 to-indigo-400 animate-pulse" />
          </div>
        </div>

        {/* 加载文字 */}
        {text !== false && (
          <div className={`${textSizes[size]} text-slate-400 font-medium animate-pulse`}>
            {text || t('common.loading')}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 页面级加载状态
 */
export function PageLoading({ text }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <LoadingSpinner text={text} size="large" />
    </div>
  );
}

/**
 * 卡片级加载状态
 */
export function CardLoading({ text }) {
  return (
    <div className="py-12 flex items-center justify-center">
      <LoadingSpinner text={text} size="medium" />
    </div>
  );
}

/**
 * 按钮级加载状态
 */
export function ButtonLoading() {
  return (
    <div className="flex items-center gap-2">
      <LoadingSpinner text={false} size="small" />
    </div>
  );
}
